<template>
  <div>
    <div class="review-page">
      <header>
        <NuxtLink :to="localePath('/')">
          <img
            src="/_nuxt3-static/logos/logo.svg"
            loading="lazy"
            class="h-6"
            alt="konfetti logo"
          />
        </NuxtLink>
      </header>
      <main class="p-4 md:p-20">
        <div v-if="isAuthenticated">
          <ReviewForm
            v-if="currentStep === 1"
            v-model:review-form="localReviewForm"
            :item="reviewItem"
            :allow-changing-event="true"
            @submit="submit"
            @click:select-event="isSidebarOpen = true"
          />
          <ReviewSuccess v-if="currentStep === 2" />
        </div>
        <div v-else class="max-w-[36rem] mx-auto">
          <h1 class="text-2xl font-semibold">
            {{ $t('review.loginPage.title') }}
          </h1>
          <ReviewPartnerHeading :partner="partnerData?.data" />
          <div class="card">
            <h2 class="text-xl font-semibold mb-4 md:mb-8">
              {{ $t('review.loginPage.loginFormTitle') }}
            </h2>
            <EmailCheckForm
              v-if="userExistsByEmail === null"
              :is-loading="isLoading"
              :email="currentEmail"
              @submit="handleEmailCheck"
              @email-change="handleEmailChange"
            />

            <LoginForm
              v-if="userExistsByEmail === true"
              :email="currentEmail"
              :is-loading="isLoading"
              @submit="handleLogin"
              @email-change="handleEmailChange"
              @forgot-password="handleForgotPassword"
            />

            <SocialLogin
              v-if="userExistsByEmail === null || userExistsByEmail === true"
              :go-back="true"
            />

            <RegisterForm
              v-else-if="userExistsByEmail === false"
              :is-loading="isLoading"
              :email="currentEmail"
              :hide="{ company_name: true, phone: true }"
              @submit="handleRegister"
              @email-change="handleEmailChange"
            />
          </div>
        </div>
      </main>
    </div>
    <ReviewEventSelectionSidebar
      :is-open="isSidebarOpen"
      :events="partnerEvents"
      :selected-event-id="selectedEventId"
      @close="closeSidebar"
      @click:event="handleEventSelect"
    />
  </div>
</template>
<script setup lang="ts">
import { toast, type ToastOptions } from 'vue3-toastify'
import { useNotificationsStore } from '../../../../stores/notifications'
import type {
  EmailFormData,
  LoginFormData,
  RegisterFormData,
} from '../../schemas/checkoutLogin'
import type { EventDescription } from '~/composables/types/event'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import 'vue3-toastify/dist/index.css'
import ReviewForm from '~/components/review/ReviewForm.vue'
import ReviewSuccess from '~/components/review/ReviewSuccess.vue'
import { useCheckoutAuth } from '~/composables/useCheckoutAuth'
import useAuthTokens from '~/composables/auth/useAuthTokens'
import useAuth from '~/composables/auth/useAuth'
import {
  extractErrorMessage,
  getReviewSubmitParams,
} from '~/utils/reviewPartnerHelpers'

const $gtm = useGtm()
const { t } = useI18n()
const $localePath = useLocalePath()
const localePath = $localePath
const { $i18n: i18n } = useNuxtApp()
const isSubdomain = useIsSubdomain()
const { locale: ISOLocale } = i18n
const locale = convertISO2Locale(ISOLocale.value)
const localeBCP47 = convertLocaleIntoBCP47(ISOLocale.value)
const isSidebarOpen = ref(false)
const selectedEventId = ref(undefined)
const eventReviewData = ref(undefined)
const currentEvent = ref(null)
const { slug } = useRoute().params
const currentEmail = ref('')

const { data: partnerEvents } = await useKftApiFetch<
  KftApiDefaultResponse<EventDescription[]>
>(
  `/v1/store/events?limit=100&search=supplier_id:${slug};${isSubdomain.value ? 'embeddedPublished:1' : 'konfettiPublished:1'}`,
  {
    transform: (res) => {
      return res?.data
    },
  }
)
const { data: partnerData = undefined } = await useKftApiFetch<
  KftApiDefaultResponse<Supplier>
>(`v1/store/supplier/${slug}?include=logo`)

const partnerItem = computed(() => ({
  data: {
    id: partnerData.value?.data?.id,
    main_image: {
      thumb: partnerData.value?.data?.logo?.data?.url,
    },
  },
}))

const reviewItem = computed(() => {
  if (currentEvent?.value?.data) return currentEvent.value
  if (eventReviewData?.value?.data) return eventReviewData.value?.data
  if (partnerItem.value?.data) return partnerItem.value
  return {}
})

const { addNotification, clearNonPersistentNotifications } =
  useNotificationsStore()

const closeSidebar = () => {
  isSidebarOpen.value = false
}

const currentStep = ref(1)
const localReviewForm = ref({
  review: '',
  rating: 0,
  media: [] as { file_name: string; media_type: 'photo' | 'video' }[],
})

const isReviewSending = ref(false)

const { isAuthenticated, checkAuthStatus } = useAuth()

const {
  userExistsByEmail,
  isLoading,
  checkUserExists,
  handleLogin: authLogin,
  handleRegister: authRegister,
  error: authError,
} = useCheckoutAuth()
const passwordResetSent = ref(false)
const { error: passwordResetError, requestPasswordReset } = usePasswordReset()
const fullLocalizedUrl = useFullLocalizedUrl()
const addCurrentSubdomain = useAddCurrentSubdomain()

const handleEmailChange = (email: string) => {
  currentEmail.value = email
  userExistsByEmail.value = null
}

const handleEmailCheck = async (data: EmailFormData) => {
  await checkUserExists(data.email)
}

const handleLogin = async (data: LoginFormData) => {
  const success = await authLogin(data.email, data.password)

  if (success) {
    await getReviewEventData()
  } else {
    toast(authError.value ?? t('login.message.error'), {
      autoClose: 3000,
      type: 'warning',
      position: toast.POSITION.TOP_CENTER,
      multiple: false,
    } as ToastOptions)
  }
}

const handleRegister = async (data: RegisterFormData) => {
  const success = await authRegister(data)

  if (success) {
    await getReviewEventData()
  } else {
    addNotification({
      type: 'ERROR',
      message: authError.value || t('login.message.error'),
    })
  }
}

const handleForgotPassword = async () => {
  if (!currentEmail.value) {
    return
  }

  await requestPasswordReset({
    email: currentEmail.value,
    reseturl: addCurrentSubdomain(fullLocalizedUrl(`/password-reset`)),
  })

  if (passwordResetError.value && passwordResetError.value?.length > 0) {
    toast(passwordResetError.value?.[0]?.message, {
      autoClose: 3000,
      type: 'error',
      position: toast.POSITION.TOP_CENTER,
      multiple: false,
    } as ToastOptions)
  } else {
    toast(t('login.forgotPassword.passwordResetSent'), {
      autoClose: 5000,
      type: 'success',
      position: toast.POSITION.TOP_CENTER,
      multiple: false,
    } as ToastOptions)

    passwordResetSent.value = true
  }
}

const handleEventSelect = (event: EventDescription) => {
  currentEvent.value = { data: event }
  closeSidebar()
}

const getReviewEventData = async () => {
  const { getAccessToken } = useAuthTokens()
  const token = await getAccessToken()
  if (!token) return
  const response = await useKftApiFetch(
    `/v1/store/reviews/event-data/?model_type=supplier&model_id=${slug}&include=mainMedia`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Accept-Language': `${localeBCP47},${locale}`,
      },
    }
  )
  eventReviewData.value = {
    ...response,
  }
}

const getSubmitParams = () =>
  getReviewSubmitParams({
    currentEvent: currentEvent.value,
    eventReviewData: eventReviewData.value,
  })

const submit = async () => {
  if (isReviewSending.value) return
  isReviewSending.value = true
  const { getAccessToken } = useAuthTokens()
  const token = await getAccessToken()
  if (!token) {
    toast(t('account.loginError'), {
      autoClose: 3000,
      type: 'error',
      position: toast.POSITION.TOP_CENTER,
      multiple: false,
    } as ToastOptions)
    return
  }

  const submitParams = getSubmitParams()
  const params = {
    ...submitParams,
    ...localReviewForm.value,
  }

  try {
    const response = await useKftApiFetch('/v1/store/reviews-qrcode', {
      method: 'POST',
      body: params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })

    if (response?.status?.value === 'success') {
      $gtm?.push({ event: 'reviewSubmit' })
      currentStep.value = 2
    } else {
      const errorPayload =
        response?.error?.value?.data ??
        response?.error?.value ??
        response?.data?.value ??
        response
      const apiMessage =
        extractErrorMessage(errorPayload) ??
        t('review.notification.error.description')
      throw new Error(apiMessage)
    }
  } catch (error: unknown) {
    let errorMessage = t('review.notification.error.description')

    if (error && typeof error === 'object' && 'data' in error) {
      const apiError = (error as { data?: Record<string, unknown> }).data
      const extracted = extractErrorMessage(
        apiError?.message ?? apiError?.error ?? apiError
      )
      if (extracted) {
        errorMessage = extracted
      } else if (apiError?.errors && typeof apiError.errors === 'object') {
        const messages = Object.values(apiError.errors)
          .flat()
          .filter(Boolean)
          .map(String)
        if (messages.length) errorMessage = messages.join('; ')
      }
    } else if (error instanceof Error && error.message) {
      errorMessage =
        error.message === '[object Object]'
          ? t('review.notification.error.description')
          : error.message
    }

    toast(errorMessage, {
      autoClose: 3000,
      type: 'error',
      position: toast.POSITION.TOP_CENTER,
      multiple: false,
    } as ToastOptions)
    console.error('Error submitting review:', error)
  } finally {
    isReviewSending.value = false
  }
}

onMounted(async () => {
  await checkAuthStatus()
  await getReviewEventData()
})

onUnmounted(() => {
  clearNonPersistentNotifications()
})

useSeoMeta({
  title: t('review.seo.title'),
})
definePageMeta({
  layout: 'blank',
})
</script>
<style lang="postcss" scoped>
header {
  @apply text-center border-b border-[#f3f4f6] h-[64px] bg-white flex items-center justify-center;
}

main {
  @apply bg-white md:pt-16 md:pb-20 min-h-[calc(100vh-64px)];
}

.card {
  @apply bg-white p-4 rounded-lg;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}
</style>
